import { Pipe, PipeTransform } from '@angular/core';
import { FlatAssignmentResult, Column } from '../../../../shared/types/reports';
import { SortByPipe } from '../../../../shared/pipes/sort-by.pipe';
import { compare } from '../../../../shared/utils/sort';

/**
 * Sorts flat assignment results by a given column name ascending or descending. If the column is 'Bill' the
 * results are returned unsorted because another pipe handles that. Sorting by priority is done within this pipe because
 * it requires special handling. All other columns are done using a generic sort pipe.
 * @see FlatAssignmentResult
 * @see BillSortPipe
 * @see SortByPipe
 */
@Pipe({
    name: 'assignmentSort',
    standalone: false,
})
export class AssignmentSortPipe implements PipeTransform {
    constructor(private sortByPipe: SortByPipe) {}

    transform(
        assignments: FlatAssignmentResult[],
        column: Column,
        ascending: boolean,
    ): FlatAssignmentResult[] {
        if (column === Column.Bill) return assignments;

        if (column === Column.Priority)
            return this.sortByPriority(assignments, ascending);

        const columnName = this.mapColumnName(column);
        return this.sortByPipe.transform<FlatAssignmentResult>(
            assignments,
            columnName,
            ascending,
        );
    }

    private lowerFirst = (column: string): string =>
        column.charAt(0).toLowerCase() + column.slice(1);

    private mapColumnName(column: string) {
        switch (column) {
            case Column.TaskDue:
                return AssignmentProp.Due;
            case Column.Task:
                return AssignmentProp.Task;
            case Column.BillTitle:
                return 'subject';
            default:
                return this.lowerFirst(column.replace(' ', ''));
        }
    }

    private sortByPriority(
        assignments: FlatAssignmentResult[],
        ascending: boolean,
    ): FlatAssignmentResult[] {
        return assignments.sort((first, second) => {
            const firstPriority = this.mapPriorityToNumber(
                first.requestPriority,
            );
            const secondPriority = this.mapPriorityToNumber(
                second.requestPriority,
            );
            return compare(firstPriority, secondPriority, ascending);
        });
    }

    private mapPriorityToNumber(priority: string): number {
        switch (priority.toLowerCase()) {
            case Priority[Priority.High].toLowerCase():
                return Priority.High;
            case Priority[Priority.Normal].toLowerCase():
                return Priority.Normal;
            case Priority[Priority.Low].toLowerCase():
                return Priority.Low;
            default:
                return Priority.Default;
        }
    }
}

enum Priority {
    High,
    Normal,
    Low,
    Default,
}

enum AssignmentProp {
    Due = 'assignmentDue',
    Task = 'assignmentTask',
}
